import { AppConfig } from './../../app.config';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { DashboardEventService } from './../../dashboard/dashboard-event.service';
import { SnapshotService } from './../../snapshot/snapshot.service';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BaseRestService } from '../../service/base-rest.service';
import { ServiceRegistry } from '../../service/service-registry.service';
import {
    WebApiEndpoints,
    GetIntradayLadderOutput,
    GetIntradayLadderRequest,
    IntradayLadderPosition,
} from '@liquitics/transaction';
import { getUTCOperatingBoundaryDatetimes } from '@liquitics/transaction';

import { AwsUtil } from '../../authentication/aws.service';
import * as moment from 'moment';


@Injectable()
export class IntradayLadderService extends BaseRestService<GetIntradayLadderOutput> {
    constructor(protected http: HttpClient,
        protected awsUtil: AwsUtil,
        protected serviceRegistry: ServiceRegistry,
        protected dashboardEventService: DashboardEventService,
        private snapshotService: SnapshotService,
        private appConfig: AppConfig) {
        super(http, awsUtil, serviceRegistry, dashboardEventService);
    }

    getAPIName(params?: any): string {
        return WebApiEndpoints.QueryIntradayLadder;
    }

    useDemoDataFile(): boolean {
        return false;
    }

    getIntradayTimeBucketedPositions(currency: string, lowerBound: moment.Moment, upperBound: moment.Moment): Promise<any> {
        const operatingDate = this.appConfig.getTodayString();
        const request = new GetIntradayLadderRequest();
        request.currency = currency;
        request.date = operatingDate;
        const anyProfile: any = this.appConfig.activeUserProfile;
        let settlementProfiles = anyProfile.settlementProfile;
        if (!settlementProfiles) {
            settlementProfiles = [
                [, , '18-00', 1]
            ]
        }
        settlementProfiles = settlementProfiles.sort((p1: any[], p2: any[2]) => {
            return p1[3] - p2[3];
        });
        const accountNames = this.snapshotService.applicationConfiguration.accountNames;

        return this.getDataObservable(request)
            .pipe(take(1))
            .toPromise()
            .then((output: GetIntradayLadderOutput) => {
                const intradayPositionsToProcess = [];
                if (output.expectedPositions) {
                    output.expectedPositions
                        .forEach((position: IntradayLadderPosition) => {
                            const positionAny: any = position;
                            positionAny.type = 'EXPECTED';
                            intradayPositionsToProcess.push(position);
                        });
                }
                if (output.actualPositions) {
                    output.actualPositions
                        .forEach((position: IntradayLadderPosition) => {
                            const positionAny: any = position;
                            positionAny.type = 'ACTUAL';
                            intradayPositionsToProcess.push(position);
                        });
                }

                const boundaryDatetimes: moment.Moment[] = getUTCOperatingBoundaryDatetimes(operatingDate,
                    this.appConfig.getTenantInfo().utcOffset,
                    this.appConfig.getTenantInfo().utcEndOfDayHour)
                    .map((datetime: string) => {
                        return moment.utc(datetime);
                    })

                return intradayPositionsToProcess
                    .map((position: IntradayLadderPosition) => {
                        const positionAny: any = position;
                        if (positionAny.type === 'EXPECTED') {
                            const foundProfile = settlementProfiles.find((p: any[]) => {
                                const account = p[0];
                                if (account) {
                                    if (position.account !== account) {
                                        return false;
                                    }
                                }
                                const clientGroup = p[1];
                                if (clientGroup) {
                                    if (position.clientGroup !== clientGroup) {
                                        return false;
                                    }
                                }
                                return true;
                            });
                            if (foundProfile) {
                                return {
                                    type: 'EXPECTED',
                                    time: moment.utc(foundProfile[2], 'h-mm'),
                                    amount: position.amount,
                                    account: accountNames[position.account]
                                };
                            } else {
                                return {
                                    type: 'EXPECTED',
                                    time: moment.utc('23-00', 'h-mm'),
                                    amount: position.amount,
                                    account: accountNames[position.account]
                                };
                            }
                        } else {
                            return {
                                type: 'ACTUAL',
                                time: moment.utc(position.timeBucket),
                                amount: position.amount,
                                account: accountNames[position.account]
                            };
                        }
                    })
                    .map((timeData: any) => {
                        if (boundaryDatetimes[0].isAfter(timeData.time)) {
                            timeData.time = timeData.time.add(1, 'day');
                        } else if (boundaryDatetimes[1].isBefore(timeData.time)) {
                            timeData.time = timeData.time.subtract(1, 'day');
                        }
                        return timeData;
                    })
            })
            .then((timeData: any[]) => {
                const results: any = {};
                results.byTime = this.groupAndReduce(timeData, false, lowerBound, upperBound);
                results.byAccount = this.groupAndReduce(timeData, true, lowerBound, upperBound);
                return results;
            })
    }

    groupAndReduce(timeData: any[], byAccount: boolean, lowerBound: moment.Moment, upperBound: moment.Moment): any[] {
        const now = moment();
        const dataByTime = timeData
            .filter((td: any) => {
                const b = lowerBound.isSameOrBefore(td.time) && upperBound.isSameOrAfter(td.time);
                return b;
            })
            .sort((td1: any, td2: any) => {
                return td1.time.diff(td2.time);
            })
            .reduce((grouped, data) => {
                let key;
                if (byAccount) {
                    key = data.time.local().format('HH-mm') + data.account;
                } else {
                    key = data.time.local().format('HH-mm');
                }
                grouped[key] = grouped[key] || [];
                grouped[key].push(data);
                if (data.type === 'EXPECTED') {
                    data.expected = data.amount;
                    data.actual = 0.0;
                } else {
                    data.expected = 0.0;
                    data.actual = data.amount;
                }
                return grouped;
            }, {});

        const byTime = Object.keys(dataByTime)
            .map((key: string) => {
                const reduced = dataByTime[key].reduce((prev, curr) => {
                    prev.expected += curr.expected;
                    prev.actual += curr.actual;
                    return prev;
                });
                return {
                    key: key,
                    time: reduced.time,
                    account: reduced.account,
                    expected: reduced.expected,
                    actual: reduced.actual
                };
            });
        if (byAccount) {
            return byTime;
            // .map((row: any) => {
            //     const timeAndAccount = row.time;
            //     row.time = timeAndAccount.substring(0, 5);
            //     row.account = timeAndAccount.substring(5);
            //     return row;
            // });
        } else {
            return byTime;
        }
    }
}
